import * as z from "zod/v4";

const stringOrNumberAsString = z.union([
  z.string(),
  z.number().transform(String),
]);

const NoteAttributeSchema_ = z.looseObject({
  name: z.string(),
  value: z.union([z.string(), z.boolean(), z.number()]).nullish(),
});
export const NoteAttributeSchema: z.ZodType<NoteAttribute> =
  NoteAttributeSchema_;
export interface NoteAttribute extends z.infer<typeof NoteAttributeSchema_> {}

const CheckoutLineItemSchema_ = z.looseObject({
  product_id: z.number().nullish(),
  variant_id: z.number().nullish(),
  title: z.string().nullish(),
  variant_title: z.string().nullish(),
  presentment_title: z.string().nullish(),
  presentment_variant_title: z.string().nullish(),
  sku: z.string().nullish(),
  price: stringOrNumberAsString.nullish(),
  quantity: z.number().nullish(),
  vendor: z.string().nullish(),
  gift_card: z.boolean().nullish(),
});
export const CheckoutLineItemSchema: z.ZodType<CheckoutLineItem> =
  CheckoutLineItemSchema_;
export interface CheckoutLineItem
  extends z.infer<typeof CheckoutLineItemSchema_> {}

const CheckoutAddressObjectSchema_ = z.looseObject({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  province: z.string().nullish(),
  country: z.string().nullish(),
  zip: z.string().nullish(),
  phone: z.string().nullish(),
  company: z.string().nullish(),
});

// Shopify sometimes sends an empty array [] instead of null for addresses
const CheckoutAddressSchema_ = z.preprocess(
  (val) => (Array.isArray(val) ? null : val),
  CheckoutAddressObjectSchema_.nullish(),
);
export const CheckoutAddressSchema: z.ZodType<CheckoutAddress> =
  CheckoutAddressSchema_ as z.ZodType<CheckoutAddress>;
export type CheckoutAddress = z.infer<typeof CheckoutAddressObjectSchema_>;

const CheckoutShippingLineSchema_ = z.looseObject({
  price: stringOrNumberAsString,
});
export const CheckoutShippingLineSchema: z.ZodType<CheckoutShippingLine> =
  CheckoutShippingLineSchema_;
export interface CheckoutShippingLine
  extends z.infer<typeof CheckoutShippingLineSchema_> {}

const CheckoutCustomerDefaultAddressSchema_ = z.looseObject({
  phone: z.string().nullish(),
});
export const CheckoutCustomerDefaultAddressSchema: z.ZodType<CheckoutCustomerDefaultAddress> =
  CheckoutCustomerDefaultAddressSchema_;
export interface CheckoutCustomerDefaultAddress
  extends z.infer<typeof CheckoutCustomerDefaultAddressSchema_> {}

const CheckoutCustomerSchema_ = z.looseObject({
  id: z.number(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  orders_count: z.number().nullish(),
  accepts_marketing: z.boolean().nullish(),
  default_address: CheckoutCustomerDefaultAddressSchema_.nullish(),
});
export const CheckoutCustomerSchema: z.ZodType<CheckoutCustomer> =
  CheckoutCustomerSchema_;
export interface CheckoutCustomer
  extends z.infer<typeof CheckoutCustomerSchema_> {}

/**
 * Shopify checkout webhook payload schema (checkouts/create, checkouts/update).
 * This is a minimal schema containing only the fields we actually use.
 *
 * @see {@link https://shopify.dev/docs/api/admin-rest/2024-01/resources/abandoned-checkouts Shopify Abandoned Checkouts API}
 */
export const ShopifyCheckoutWebhookSchema_ = z.looseObject({
  // Discriminator for distinguishing from pixel event checkouts
  _type: z.literal("webhook").default("webhook"),

  // Core identifiers
  id: z.number(),
  token: z.string(),
  cart_token: z.string().nullish(), // TODO: booka66 - when checking out with shop app for merchants not on marketing, this field can be used to link events to the right customer using their product_added_to_cart event and cart.id field.

  // Contact info
  email: z.string().nullish(),
  phone: z.string().nullish(),

  // Timestamps
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),

  // Currency and prices
  currency: z.string().nullish(),
  subtotal_price: stringOrNumberAsString.nullish(),
  total_price: stringOrNumberAsString.nullish(),
  total_tax: stringOrNumberAsString.nullish(),

  // Cart attributes (for anonymousId lookup)
  note_attributes: z.array(NoteAttributeSchema_).nullish(),

  // Line items
  line_items: z.array(CheckoutLineItemSchema_).nullish(),

  // Shipping
  shipping_lines: z.array(CheckoutShippingLineSchema_).nullish(),
  shipping_address: CheckoutAddressSchema_.nullish(), // Sometimes an empty array [] (why Shopify, why)
  billing_address: CheckoutAddressSchema_.nullish(),

  // Customer
  customer: CheckoutCustomerSchema_.nullish(),
});
export const ShopifyCheckoutWebhookSchema: z.ZodType<ShopifyCheckoutWebhook> =
  ShopifyCheckoutWebhookSchema_;
export interface ShopifyCheckoutWebhook
  extends z.infer<typeof ShopifyCheckoutWebhookSchema_> {}

/**
 * Type guard for Shopify checkout webhook payload.
 * Validates the essential fields required to process a checkout webhook.
 */
export function isShopifyCheckoutWebhook(
  data: unknown,
): data is ShopifyCheckoutWebhook {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const d = data as Record<string, unknown>;
  return (
    typeof d.id === "number" &&
    typeof d.token === "string" &&
    typeof d.created_at === "string" &&
    typeof d.updated_at === "string" &&
    typeof d.currency === "string" &&
    (typeof d.subtotal_price === "string" ||
      typeof d.subtotal_price === "number") &&
    (typeof d.total_price === "string" || typeof d.total_price === "number")
  );
}

/**
 * Parse a price value that may be a string or number into a number.
 * Returns 0 if the value is null or undefined.
 */
export function parseCheckoutPrice(
  value: string | number | null | undefined,
): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number") {
    return value;
  }
  return parseFloat(value) || 0;
}
